/**
 * Shared HTTP plumbing for the two browser clients (backlog D19).
 *
 * `lib/api/client.ts` (`api.*`) talks to the backend through the BFF proxy
 * (`/api/proxy/v1/*`); `lib/api/bff.ts` (`bff.*`) talks to the Next route
 * handlers that own the server-side session (`/api/auth/*`, `/api/branding/*`).
 * Different base paths, identical contract — cookie session, typed
 * {@link ApiError} carrying the parsed problem body, empty-body tolerance — so
 * the response handling lives here once instead of being re-implemented (and
 * re-diverged) at every call site.
 *
 * Nothing in this module is exported to feature code: hooks and components use
 * `api.*` / `bff.*`, never `fetch` (eslint `no-restricted-globals` enforces it).
 */

import { notify } from "@/lib/feedback/notify";
import { t } from "@/lib/i18n";
import { TENANT_MISMATCH_EVENT, isTenantMismatchBody } from "./tenant-assertion";

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown,
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = "ApiError";
  }
}

/**
 * Per-call options every verb accepts. `signal` is the TanStack Query
 * `QueryFunctionContext.signal`: passing it lets a superseded query (typing in
 * a search box, switching a filter) abort its in-flight HTTP request instead of
 * running to completion against a discarded result.
 */
export interface ApiCallOptions {
  signal?: AbortSignal;
}

/** Fired on a 401 so the shell can bounce the user back through the BFF login. */
function notifySessionExpired(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("auth:session-expired"));
  }
}

/**
 * Fired on the proxy's 409 `tenant_mismatch` (P0-3): the shared session's active
 * tenant moved on in another tab, so this tab's chrome, cache and query keys all
 * belong to a tenant the server no longer accepts. The shell reloads on it.
 */
function notifyTenantMismatch(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(TENANT_MISMATCH_EVENT));
  }
}

/**
 * P1-4: the problem `type`/`title` the backend answers a WRITE attempted inside
 * a read-only tenant with. Distinct from `tenant_suspended`/`tenant_locked`,
 * which refuse the request outright — this one means „you are in, you may read,
 * this particular call was a write“.
 */
const TENANT_READ_ONLY_CODE = "tenant_read_only";
const TENANT_READ_ONLY_TYPE = "urn:ismshub:problem:tenant_read_only";

/** True when `body` is the backend's read-only refusal problem document. */
export function isTenantReadOnlyBody(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const doc = body as { title?: unknown; code?: unknown; type?: unknown };
  return (
    doc.title === TENANT_READ_ONLY_CODE ||
    doc.code === TENANT_READ_ONLY_CODE ||
    doc.type === TENANT_READ_ONLY_TYPE
  );
}

/**
 * Says out loud what a read-only tenant just did to this call.
 *
 * A bare `ApiError` would surface as whatever generic „konnte nicht gespeichert
 * werden“ the calling form happens to have — technically true, actively
 * misleading, and it invites the user to retry forever. The shell already
 * carries the persistent banner explaining the mode; this is the per-action
 * confirmation that the refusal was the mode and not a bug.
 *
 * Deliberately proportionate: a toast, not a lockout flow. And deliberately
 * ONE toast — a form that fires three writes on save must not stack three
 * identical notices, so they share a sonner id.
 */
function notifyTenantReadOnly(): void {
  if (typeof window === "undefined") return;
  notify.error(t("shell.serving.writeRefused"), { id: "tenant-read-only" });
}

/**
 * True for a rejection that means "this request was cancelled", not "the network
 * failed" — a superseded TanStack query passing its `AbortSignal`, or an
 * `AbortSignal.timeout()`. Those are rethrown untouched: the query layer keys
 * its cancellation handling off `error.name`, and rewriting it would turn a
 * normal cancellation into a reported failure.
 */
function isAbortLike(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const name = (error as { name?: unknown }).name;
  return name === "AbortError" || name === "TimeoutError";
}

/**
 * `fetch`, with a transport failure turned into something the UI can recognise
 * (spec §A4).
 *
 * The defect this closes: the browser clients called `fetch` bare, so when the
 * network is gone `fetch` rejects with a `TypeError`, `throwApiError` never runs,
 * no `ApiError` is ever constructed — and every call site's `instanceof ApiError`
 * narrowing falls through to the same generic sentence a 500 produces. „Kein
 * Netz" and „der Server ist kaputt" are different problems with different user
 * actions, and the UI could not tell them apart.
 *
 * The normalisation is deliberately shallow: same success path, same `ApiError`
 * for every response the server actually sent, no retries, no `networkMode`. It
 * only guarantees that what comes out of a failed transport is an `Error` named
 * `NetworkError` carrying the original as `cause` — a name
 * `lib/api/problem-message.ts#isNetworkError` classifies, whatever shape the
 * runtime threw (browsers reject with `TypeError`, undici sometimes with a
 * wrapped `Error`).
 */
export async function fetchOrTransportError(
  input: string,
  init: RequestInit,
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    if (isAbortLike(error)) throw error;
    if (error instanceof Error && error.name === "NetworkError") throw error;
    const failure = new Error("Network request failed", { cause: error });
    failure.name = "NetworkError";
    throw failure;
  }
}

/**
 * Read the error response body. If the server returns JSON (typically
 * application/problem+json from the backend's ProblemDetails handler), parse it
 * so error-message extraction can read title/detail/errors fields. Otherwise
 * fall back to the raw text.
 */
export async function parseErrorBody(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => null);
  if (!text) return null;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("json")) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

/**
 * Throws an `ApiError` for a failed response, emitting session-expired on 401.
 *
 * `silentUnauthorized` suppresses that event for endpoints that answer 401 as
 * their normal negative result — the login route in particular, where "your
 * session expired" would be both untrue and disruptive.
 */
export async function throwApiError(
  response: Response,
  opts?: { silentUnauthorized?: boolean },
): Promise<never> {
  if (response.status === 401 && !opts?.silentUnauthorized) {
    notifySessionExpired();
  }
  const body = await parseErrorBody(response);
  if (response.status === 409 && isTenantMismatchBody(body)) {
    notifyTenantMismatch();
  }
  // P1-4: a write refused because the tenant is in its read-only wind-down. The
  // ApiError still propagates — callers keep their own error handling — but the
  // user gets the real reason instead of a generic save failure.
  if (response.status === 423 && isTenantReadOnlyBody(body)) {
    notifyTenantReadOnly();
  }
  throw new ApiError(response.status, response.statusText, body);
}

/**
 * Read a successful response body as JSON. Returns `undefined` (typed as `T`)
 * when the body is empty — covers 204 No Content, 205 Reset Content, and any
 * 2xx the backend returns without a body (e.g. 202 Accepted from fire-and-forget
 * endpoints). Calling `response.json()` on an empty body throws
 * "Unexpected end of JSON input".
 */
export async function readJsonOrEmpty<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }
  if (response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}
